import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Check, ChevronLeft, ChevronRight, Crown, X, Zap } from 'lucide-react';
import { formatCredits } from '@/pages/ipe/workbench/utils/accountCenterUtils';
import {
  CREDIT_PACK_MONTHLY,
  calcMembershipOriginalTotal,
  calcMembershipTotalPrice,
  formatPricePerBean,
  formatYuanAmount,
  monthlyPriceForCredits,
} from '@/utils/membershipDuration';
import { showToast } from '@/utils/toast';

const FREE_TIER_CODES = new Set(['free', 'trial']);

const TIER_SUBTITLES = {
  free: '适合个人体验，试用基础 AI 功能',
  silver: '适合个人卖家或小团队入门使用',
  gold: '适合成长型团队，更高配额与折扣',
  diamond: '适合企业级用户，最大配额与最优折扣',
};

function isFreeTier(row) {
  if (!row) return false;
  return FREE_TIER_CODES.has(String(row.tier || '').toLowerCase())
    || String(row.label || '').includes('免费');
}

function isPurchasableMembershipTier(row) {
  if (!row) return false;
  if (isFreeTier(row)) return false;
  const price = Number(row.priceYuan);
  if (!Number.isNaN(price) && price <= 0) return false;
  return true;
}

function formatStorageLabel(mb) {
  const n = Number(mb) || 0;
  if (n >= 1024) {
    const gb = n / 1024;
    return Number.isInteger(gb) ? `${gb}GB` : `${gb.toFixed(1)}GB`;
  }
  return `${n}MB`;
}

function formatDiscountLabel(discountPercent) {
  const p = Number(discountPercent) || 100;
  if (p >= 100) return null;
  if (p % 10 === 0) return `${p / 10}折`;
  return `${p}折`;
}

function tierDisplayName(tier) {
  if (isFreeTier(tier)) return '免费版';
  return tier.label;
}

function tierSubtitle(tier) {
  if (tier?.note && !String(tier.note).startsWith('¥')) return tier.note;
  return TIER_SUBTITLES[String(tier?.tier || '').toLowerCase()] || '解锁更多权益与配额';
}

function buildFreeFeatureSections(tier) {
  const bonus = Number(tier?.bonusCredits) || 100;
  const storage = formatStorageLabel(tier?.storageLimitMb || 512);
  return [
    {
      title: '包含权益',
      rows: [
        { label: '注册赠送 IPE豆', value: `${formatCredits(bonus)} 豆`, ok: true },
        { label: '图片空间', value: storage, ok: true },
        { label: '基础 AI 图片工具', ok: true },
        { label: '店铺授权', ok: false },
      ],
    },
  ];
}

function buildPaidFeatureSections(tier) {
  const discount = formatDiscountLabel(tier.discountPercent);
  return [
    {
      title: '配额权益',
      rows: [
        { label: '店铺授权', value: `${tier.shopLimit ?? 0} 家`, ok: true },
        ...(tier.bonusCredits
          ? [{ label: '每月赠送 IPE豆', value: `${formatCredits(tier.bonusCredits)} 豆`, ok: true }]
          : []),
        ...(tier.storageLimitMb
          ? [{ label: '图片空间', value: formatStorageLabel(tier.storageLimitMb), ok: true }]
          : []),
        ...(discount
          ? [{ label: 'IPE豆套餐折扣', value: discount, ok: true, highlight: true }]
          : []),
      ],
    },
    {
      title: '功能权益',
      rows: [
        { label: '全平台 AI 创作工具', ok: true },
        { label: '工作流与批量处理', ok: true },
        { label: '团队邀请与协作', ok: true },
      ],
    },
  ];
}

function normalizeCreditPacks(apiPacks) {
  if (apiPacks?.length) {
    return apiPacks.map((pack) => ({
      id: pack.id || `credit_${pack.amount}`,
      credits: Number(pack.amount) || 0,
      monthlyPriceYuan: Number(pack.monthlyPriceYuan) || 0,
      pricePerBean: pack.pricePerBean || '',
    })).filter((p) => p.credits > 0 && p.monthlyPriceYuan > 0);
  }
  return CREDIT_PACK_MONTHLY.map((pack, index) => ({
    id: `fallback_${index}`,
    credits: pack.credits,
    monthlyPriceYuan: pack.monthlyPriceYuan,
    pricePerBean: formatPricePerBean(pack.credits, pack.monthlyPriceYuan),
  }));
}

function calcReferenceOriginalPrice(credits, monthlyPrice, packs) {
  const first = packs[0];
  if (!first?.credits || !first?.monthlyPriceYuan) {
    return calcMembershipOriginalTotal(monthlyPrice, 1);
  }
  const baseUnit = Number(first.monthlyPriceYuan) / Number(first.credits);
  return Math.round(Number(credits) * baseUnit * 100) / 100;
}

function formatDiscountBadge(displayPrice, originalPrice) {
  if (!originalPrice || originalPrice <= displayPrice) return null;
  const ratio = Math.round((displayPrice / originalPrice) * 100);
  if (ratio >= 100) return null;
  if (ratio % 10 === 0) return `下单享${ratio / 10}折`;
  return `下单享${ratio}折`;
}

function FeatureSections({ sections }) {
  return (
    <div className="ipe-membership-tier__features">
      {sections.map((section) => (
        <div key={section.title} className="ipe-membership-tier__feature-group">
          <h4 className="ipe-membership-tier__feature-title">{section.title}</h4>
          <ul className="ipe-membership-tier__feature-list">
            {section.rows.map((row) => (
              <li key={row.label} className="ipe-membership-tier__feature-row">
                <span className="ipe-membership-tier__feature-label">{row.label}</span>
                <span className="ipe-membership-tier__feature-value">
                  {row.value ? (
                    <span className={`ipe-membership-tier__feature-text${row.highlight ? ' ipe-membership-tier__feature-text--highlight' : ''}`}>
                      {row.value}
                    </span>
                  ) : null}
                  {row.ok ? (
                    <span className="ipe-membership-tier__feature-icon ipe-membership-tier__feature-icon--ok" aria-hidden="true">
                      <Check size={14} strokeWidth={2.5} />
                    </span>
                  ) : (
                    <span className="ipe-membership-tier__feature-icon ipe-membership-tier__feature-icon--no" aria-hidden="true">
                      <X size={14} strokeWidth={2.5} />
                    </span>
                  )}
                </span>
              </li>
            ))}
          </ul>
        </div>
      ))}
    </div>
  );
}

function MembershipTierCard({ tier, variant, isCurrent, freeTierLabel, onBuy }) {
  const isFree = variant === 'free';
  const monthlyPrice = Number(tier.priceYuan) || 0;
  const sections = isFree ? buildFreeFeatureSections(tier) : buildPaidFeatureSections(tier);

  return (
    <article className={`ipe-membership-tier${isFree ? ' ipe-membership-tier--free' : ' ipe-membership-tier--paid'}${isCurrent ? ' ipe-membership-tier--current' : ''}`}>
      <div className="ipe-membership-tier__head">
        <span className={`ipe-membership-tier__icon${isFree ? ' ipe-membership-tier__icon--free' : ' ipe-membership-tier__icon--paid'}`} aria-hidden="true">
          <Crown size={28} strokeWidth={1.8} />
        </span>
        <h3 className="ipe-membership-tier__name">{tierDisplayName(tier)}</h3>
        <p className="ipe-membership-tier__desc">{tierSubtitle(tier)}</p>
      </div>

      <div className="ipe-membership-tier__price-block">
        {isFree ? (
          <p className="ipe-membership-tier__price">
            <strong>0</strong>
            <span className="ipe-membership-tier__price-unit">元/月</span>
          </p>
        ) : (
          <p className="ipe-membership-tier__price">
            <strong>{formatYuanAmount(monthlyPrice)}</strong>
            <span className="ipe-membership-tier__price-unit">元/月起</span>
          </p>
        )}
      </div>

      {isFree ? (
        <button type="button" className="ipe-membership-tier__cta ipe-membership-tier__cta--muted" disabled>
          {isCurrent ? '当前版本' : '免费使用'}
        </button>
      ) : (
        <button
          type="button"
          className="ipe-membership-tier__cta"
          disabled={isCurrent}
          onClick={() => onBuy(tier, 'membership')}
        >
          {isCurrent ? '当前档位' : '立即开通'}
        </button>
      )}

      {!isFree && freeTierLabel ? (
        <div className="ipe-membership-tier__extra-bar">
          拥有「{freeTierLabel}」全部权益，额外享有
        </div>
      ) : null}

      <FeatureSections sections={sections} />
    </article>
  );
}

function CreditPackCard({ pack, packs, onBuy }) {
  const credits = pack.credits;
  const monthlyPrice = Number(pack.monthlyPriceYuan) || monthlyPriceForCredits(credits, packs);
  const displayPrice = calcMembershipTotalPrice(monthlyPrice, 1);
  const originalPrice = calcReferenceOriginalPrice(credits, monthlyPrice, packs);
  const showOriginal = originalPrice > displayPrice + 0.001;
  const unitLabel = pack.pricePerBean || formatPricePerBean(credits, monthlyPrice);
  const discountBadge = showOriginal ? formatDiscountBadge(displayPrice, originalPrice) : null;

  return (
    <article className="ipe-credit-plan__card">
      {discountBadge ? (
        <span className="ipe-credit-plan__badge">{discountBadge}</span>
      ) : null}
      <div className="ipe-credit-plan__card-head">
        <span className="ipe-credit-plan__card-icon" aria-hidden="true">
          <Zap size={14} strokeWidth={2.6} />
        </span>
        <strong className="ipe-credit-plan__card-amount">{formatCredits(credits)}</strong>
      </div>
      <div className="ipe-credit-plan__card-price-row">
        <span className="ipe-credit-plan__card-price">
          <small>¥</small>
          {formatYuanAmount(displayPrice)}
        </span>
        {showOriginal ? (
          <span className="ipe-credit-plan__card-origin">¥{formatYuanAmount(originalPrice)}</span>
        ) : null}
      </div>
      <p className="ipe-credit-plan__card-unit">{unitLabel.replace('/豆', '/IPE豆')}</p>
      <button type="button" className="ipe-credit-plan__card-buy" onClick={() => onBuy(pack, 'credit')}>
        立即购买
      </button>
    </article>
  );
}

function resolvePlanTabFromSearch(searchParams) {
  const tab = searchParams.get('tab');
  return tab === 'membership' ? 'membership' : 'credits';
}

export default function CreditPlanSection({ accountCenter }) {
  const [searchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState(() => resolvePlanTabFromSearch(searchParams));

  useEffect(() => {
    setActiveTab(resolvePlanTabFromSearch(searchParams));
  }, [searchParams]);
  const scrollRef = useRef(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const recharge = accountCenter?.recharge || {};
  const contactHint = recharge.hint || accountCenter?.quota?.upgradeContactHint || '请联系商务充值';
  const currentTier = accountCenter?.user?.planTier || 'free';

  const creditPacks = useMemo(
    () => normalizeCreditPacks(accountCenter?.creditRechargePacks),
    [accountCenter?.creditRechargePacks],
  );

  const allMembershipTiers = useMemo(() => {
    if (accountCenter?.membershipTiers?.length) return accountCenter.membershipTiers;
    if (accountCenter?.usageTiers?.length) return accountCenter.usageTiers;
    return [];
  }, [accountCenter?.membershipTiers, accountCenter?.usageTiers]);

  const freeTier = useMemo(
    () => allMembershipTiers.find(isFreeTier) || {
      tier: 'free',
      label: '免费版',
      priceYuan: 0,
      shopLimit: 0,
      storageLimitMb: 512,
      bonusCredits: 100,
    },
    [allMembershipTiers],
  );

  const paidMembershipTiers = useMemo(
    () => allMembershipTiers.filter(isPurchasableMembershipTier),
    [allMembershipTiers],
  );

  const showMembership = activeTab === 'membership';

  const updateScrollState = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 4);
    setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 4);
  }, []);

  useEffect(() => {
    if (showMembership) return undefined;
    updateScrollState();
    return undefined;
  }, [activeTab, creditPacks.length, showMembership, updateScrollState]);

  const scrollByCards = useCallback((direction) => {
    const el = scrollRef.current;
    if (!el) return;
    const cardWidth = el.querySelector('.ipe-credit-plan__card')?.offsetWidth || 220;
    el.scrollBy({ left: direction * (cardWidth + 16), behavior: 'smooth' });
    window.setTimeout(updateScrollState, 280);
  }, [updateScrollState]);

  const handleBuy = useCallback((_item, type) => {
    const typeLabel = type === 'membership' ? '会员' : 'IPE豆';
    const parts = [`${typeLabel}购买请联系商务`, contactHint];
    if (recharge.phone) parts.push(`客服电话：${recharge.phone}`);
    if (recharge.wechat) parts.push(`商务微信：${recharge.wechat}`);
    showToast(parts.filter(Boolean).join(' · '), 'info', 5000);
  }, [contactHint, recharge.phone, recharge.wechat]);

  return (
    <section className="ipe-credit-plan" aria-labelledby="ipe-credit-plan-title">
      <header className="ipe-credit-plan__header">
        <h2 id="ipe-credit-plan-title" className="ipe-credit-plan__title">IPE 会员方案</h2>
        <p className="ipe-credit-plan__subtitle">选择适合您的会员档位，或按需充值 IPE豆</p>
      </header>

      <div className="ipe-credit-plan__tabs" role="tablist" aria-label="方案类型">
        <button
          type="button"
          role="tab"
          aria-selected={activeTab === 'membership'}
          className={`ipe-credit-plan__tab${activeTab === 'membership' ? ' ipe-credit-plan__tab--active' : ''}`}
          onClick={() => setActiveTab('membership')}
        >
          会员
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={activeTab === 'credits'}
          className={`ipe-credit-plan__tab${activeTab === 'credits' ? ' ipe-credit-plan__tab--active' : ''}`}
          onClick={() => setActiveTab('credits')}
        >
          IPE豆
        </button>
      </div>

      {showMembership ? (
        <div className="ipe-membership-compare">
          <MembershipTierCard
            tier={freeTier}
            variant="free"
            isCurrent={currentTier === freeTier.tier}
            onBuy={handleBuy}
          />
          {paidMembershipTiers.length ? (
            paidMembershipTiers.map((tier) => (
              <MembershipTierCard
                key={tier.tier}
                tier={tier}
                variant="paid"
                isCurrent={currentTier === tier.tier}
                freeTierLabel={tierDisplayName(freeTier)}
                onBuy={handleBuy}
              />
            ))
          ) : (
            <p className="ipe-credit-plan__empty">暂无会员套餐，请联系商务咨询。</p>
          )}
        </div>
      ) : (
        <div className="ipe-credit-plan__carousel">
          {canScrollLeft ? (
            <button
              type="button"
              className="ipe-credit-plan__nav ipe-credit-plan__nav--prev"
              aria-label="向左滚动"
              onClick={() => scrollByCards(-1)}
            >
              <ChevronLeft size={20} strokeWidth={2} aria-hidden="true" />
            </button>
          ) : null}

          <div
            ref={scrollRef}
            className="ipe-credit-plan__scroll"
            onScroll={updateScrollState}
          >
            {creditPacks.length ? (
              creditPacks.map((pack) => (
                <CreditPackCard
                  key={pack.id}
                  pack={pack}
                  packs={creditPacks}
                  onBuy={handleBuy}
                />
              ))
            ) : (
              <p className="ipe-credit-plan__empty">暂无 IPE豆套餐，请联系商务咨询。</p>
            )}
          </div>

          {canScrollRight ? (
            <button
              type="button"
              className="ipe-credit-plan__nav ipe-credit-plan__nav--next"
              aria-label="向右滚动"
              onClick={() => scrollByCards(1)}
            >
              <ChevronRight size={20} strokeWidth={2} aria-hidden="true" />
            </button>
          ) : null}
        </div>
      )}
    </section>
  );
}
